import {createStore as createZustandStore} from 'zustand/vanilla';
import {useStore} from 'zustand';
import type {
  SubChatWindowStatus,
  WidgetPart,
} from 'src/components/agentic-ui/types';
import type {QuestionnaireDraft} from 'src/types/ask-ai';


export type SubChatState = {
  status: SubChatWindowStatus;
  /** The currently docked widget part (e.g. a questionnaire). Null while processing. */
  dockedWidget: WidgetPart | null;
  /** Id of the assistant message that emitted the docked widget. */
  sourceMessageId: string | null;
  /**
   * In-progress wizard state for the docked questionnaire. Persisted here (not
   * in the widget) so it survives minimize→maximize, which unmounts/remounts the
   * widget. Cleared whenever the docked widget changes or the dock resets.
   */
  wizardDraft: QuestionnaireDraft | null;
  /** Open (or re-dock) with a docked widget — always sets status to 'max'. */
  open: (widget: WidgetPart, sourceMessageId: string) => void;
  minimize: () => void;
  maximize: () => void;
  /** Clear everything back to ABSENT. */
  reset: () => void;
  /** Replace the docked widget without changing status (null => processing footer). */
  setDockedWidget: (widget: WidgetPart | null) => void;
  /** Persist the questionnaire wizard draft (survives minimize/maximize). */
  setWizardDraft: (draft: QuestionnaireDraft | null) => void;
  /** Restore persisted state (page refresh). */
  seed: (args: {
    status: SubChatWindowStatus;
    dockedWidget: WidgetPart | null;
    sourceMessageId: string | null;
  }) => void;
};

export const subChatStore = createZustandStore<SubChatState>((set) => ({
  status: 'absent',
  dockedWidget: null,
  sourceMessageId: null,
  wizardDraft: null,
  // Opening a new docked widget starts a fresh wizard — drop any stale draft.
  open: (widget, sourceMessageId) =>
    set({
      status: 'max',
      dockedWidget: widget,
      sourceMessageId,
      wizardDraft: null,
    }),
  minimize: () => set((s) => (s.status === 'absent' ? s : {status: 'min'})),
  maximize: () => set((s) => (s.status === 'absent' ? s : {status: 'max'})),
  reset: () =>
    set({
      status: 'absent',
      dockedWidget: null,
      sourceMessageId: null,
      wizardDraft: null,
    }),
  // Swapping (or clearing) the docked widget invalidates the current draft.
  setDockedWidget: (widget) => set({dockedWidget: widget, wizardDraft: null}),
  setWizardDraft: (draft) => set({wizardDraft: draft}),
  // Restoring (page refresh) starts a fresh wizard — same draft invalidation
  // as open()/setDockedWidget().
  seed: ({status, dockedWidget, sourceMessageId}) =>
    set({status, dockedWidget, sourceMessageId, wizardDraft: null}),
}));

// Vanilla accessor used by use-chat at send-time (outside React).
export function getSubChatWindowStatus(): SubChatWindowStatus {
  return subChatStore.getState().status;
}

// React selector hooks
export function useSubChatStatus(): SubChatWindowStatus {
  return useStore(subChatStore, (s) => s.status);
}

export function useDockedWidget(): WidgetPart | null {
  return useStore(subChatStore, (s) => s.dockedWidget);
}
